// src/services/api/referral.service.ts
import { authService } from '../auth.service';
import { getCurrentApiUrl } from '../../config/server.config';

export interface ReferralStatus {
  isReferrer: boolean;
  referralCode?: string;
  referralPercentage?: number;
  totalReferrals?: number;
  totalEarnings?: number;
  referredBy?: {
    userId: string;
    name: string;
    email: string;
    code?: string;
    date?: string;
  };
}

export interface ReferralUser {
  id: string;
  name: string;
  email: string;
  registeredAt: string;
  status: 'ACTIVE' | 'PENDING' | 'INACTIVE';
  earnings: number;
}

export interface ReferralSummary {
  totalReferrals: number;
  activeReferrals: number;
  pendingReferrals: number;
  totalEarnings: number;
  currentMonthEarnings: number;
  lastMonthEarnings: number;
  conversionRate: number;
}

export interface MonthlyReport {
  month: string;
  year: number;
  referrals: number;
  earnings: number;
  transactions: number;
}

export interface QuarterlyReport {
  quarter: number;
  year: number;
  referrals: number;
  earnings: number;
  growth: number;
}

export interface YearlyReport {
  year: number;
  totalEarnings: number;
  monthlyBreakdown: MonthlyReport[];
  comparison: {
    previousYear: number;
    previousTotal: number;
    growth: number;
  };
}

export interface NetworkStats {
  totalNetwork: number;
  activeUsers: number;
  totalTransactions: number;
  networkEarnings: number;
  topPerformers: {
    userId: string;
    name: string;
    earnings: number;
  }[];
}

export interface ExportOptions {
  format: 'CSV' | 'PDF' | 'EXCEL' | 'JSON';
  dateFrom: string;
  dateTo: string;
}

export interface ExportResponse {
  reportId: string;
  fileUrl: string;
  expiresAt: string;
}

class ReferralService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = getCurrentApiUrl();
  }

  // ====================================
  // ACTIVATION & MANAGEMENT
  // ====================================

  async activateAsReferrer(): Promise<any> {
    try {
      const response = await fetch(`${this.baseUrl}/referrals/activate`, {
        method: 'POST',
        headers: {
          'Authorization': authService.getAuthorizationHeader(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({})
      });
      return await response.json();
    } catch (error) {
      console.error('Error activating as referrer:', error);
      throw error;
    }
  }

  async createCustomCode(customCode: string): Promise<any> {
    try {
      const response = await fetch(`${this.baseUrl}/referrals/customize`, {
        method: 'POST',
        headers: {
          'Authorization': authService.getAuthorizationHeader(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ customCode })
      });
      return await response.json();
    } catch (error) {
      console.error('Error creating custom code:', error);
      throw error;
    }
  }

  async applyReferralCode(referralCode: string): Promise<any> {
    try {
      const response = await fetch(`${this.baseUrl}/referrals/apply`, {
        method: 'POST',
        headers: {
          'Authorization': authService.getAuthorizationHeader(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ referralCode })
      });
      
      const data = await response.json();
      
      // If HTTP status is not ok, throw error with backend message
      if (!response.ok) {
        throw new Error(data.message || `HTTP ${response.status}: ${response.statusText}`);
      }
      
      return data;
    } catch (error) {
      console.error('Error applying referral code:', error);
      throw error;
    }
  }

  async validateCode(code: string): Promise<any> {
    try {
      const response = await fetch(`${this.baseUrl}/referrals/validate/${code}`, {
        headers: {
          'Authorization': authService.getAuthorizationHeader(),
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      
      // If HTTP status is not ok, throw error with backend message
      if (!response.ok) {
        throw new Error(data.message || `HTTP ${response.status}: ${response.statusText}`);
      }
      
      return data;
    } catch (error) {
      console.error('Error validating code:', error);
      throw error;
    }
  }

  // ====================================
  // STATUS & REFERRALS
  // ====================================

  async getMyStatus(): Promise<ReferralStatus> {
    try {
      const response = await fetch(`${this.baseUrl}/referrals/my-status`, {
        headers: {
          'Authorization': authService.getAuthorizationHeader(),
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Error fetching referral status:', error);
      throw error;
    }
  }

  async getMyReferrals(): Promise<ReferralUser[]> {
    try {
      const response = await fetch(`${this.baseUrl}/referrals/my-referrals`, {
        headers: {
          'Authorization': authService.getAuthorizationHeader(),
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      
      // If HTTP status is not ok, throw error with backend message
      if (!response.ok) {
        throw new Error(data.message || `HTTP ${response.status}: ${response.statusText}`);
      }
      
      // Backend returns { referralCode, totalReferrals, referrals: [...] }
      // We need to access the referrals array and map it to our interface
      const referralsData = data.data;
      return referralsData.referrals.map((ref: any) => ({
        id: ref.userId,
        name: ref.name,
        email: ref.email,
        registeredAt: ref.registeredAt,
        status: ref.onboardingCompleted ? 'ACTIVE' : 'PENDING',
        earnings: ref.totalCommissionsGenerated || 0
      }));
    } catch (error) {
      console.error('Error fetching my referrals:', error);
      throw error;
    }
  }

  // ====================================
  // REPORTS & STATISTICS
  // ====================================

  async getMySummary(): Promise<ReferralSummary> {
    try {
      const response = await fetch(`${this.baseUrl}/referrals/my-reports/summary`, {
        headers: {
          'Authorization': authService.getAuthorizationHeader(),
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Error fetching summary:', error);
      throw error;
    }
  }

  async getMyMonthlyReport(year?: number): Promise<MonthlyReport[]> {
    try {
      const queryParams = year ? `?year=${year}` : '';
      const response = await fetch(`${this.baseUrl}/referrals/my-reports/monthly${queryParams}`, {
        headers: {
          'Authorization': authService.getAuthorizationHeader(),
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      return data.data.months;
    } catch (error) {
      console.error('Error fetching monthly report:', error);
      throw error;
    }
  }

  async getMyQuarterlyReport(year?: number): Promise<QuarterlyReport[]> {
    try {
      const queryParams = year ? `?year=${year}` : '';
      const response = await fetch(`${this.baseUrl}/referrals/my-reports/quarterly${queryParams}`, {
        headers: {
          'Authorization': authService.getAuthorizationHeader(),
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      return data.data.quarters;
    } catch (error) {
      console.error('Error fetching quarterly report:', error);
      throw error;
    }
  }

  async getMyYearlyReport(year?: number): Promise<YearlyReport> {
    try {
      const queryParams = year ? `?year=${year}` : '';
      const response = await fetch(`${this.baseUrl}/referrals/my-reports/yearly${queryParams}`, {
        headers: {
          'Authorization': authService.getAuthorizationHeader(),
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Error fetching yearly report:', error);
      throw error;
    }
  }

  async getMyTrend(): Promise<any> {
    try {
      const response = await fetch(`${this.baseUrl}/referrals/my-reports/trend`, {
        headers: {
          'Authorization': authService.getAuthorizationHeader(),
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Error fetching trend:', error);
      throw error;
    }
  }

  async getMyNetwork(): Promise<NetworkStats> {
    try {
      const response = await fetch(`${this.baseUrl}/referrals/my-reports/network`, {
        headers: {
          'Authorization': authService.getAuthorizationHeader(),
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Error fetching network stats:', error);
      throw error;
    }
  }

  // ====================================
  // EXPORT
  // ====================================

  async exportMyReport(options: ExportOptions): Promise<ExportResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/referrals/my-reports/export`, {
        method: 'POST',
        headers: {
          'Authorization': authService.getAuthorizationHeader(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(options)
      });
      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Error exporting report:', error);
      throw error;
    }
  }

  // Helper method to download exported file
  async downloadExportedFile(fileUrl: string, filename: string): Promise<void> {
    try {
      const response = await fetch(fileUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading file:', error);
      throw error;
    }
  }
}

export const referralService = new ReferralService();